/**
 * Credit state derivation. Mirrors the server's `resolve()` in
 * `apps/nexus/nexus/models/credits/mod.rs` — the floor there is `-500` and a
 * balance exactly AT the floor is refused, so `blocked` starts at `-500`.
 */

export type CreditState = 'normal' | 'low' | 'overdrawn' | 'blocked';

/**
 * Balance at or below which the server refuses to start new work.
 *
 * SOURCE OF TRUTH: `DEFAULT_CREDIT_FLOOR` in
 * `apps/nexus/nexus/models/credits/mod.rs`, overridable per environment via the
 * `CREDITS_BALANCE_FLOOR` env var. This copy is NOT fetched — no endpoint
 * exposes the floor — so it can silently drift.
 *
 * If the server floor changes, update this constant. Drift does not break
 * enforcement (the server still refuses at its own floor) but it does make the
 * UI lie: too high and the banner claims the user is blocked while work still
 * succeeds; too low and work is refused with no warning shown.
 */
export const CREDIT_FLOOR = -500;

/** Balance at or below which the UI warns the user they are running out. */
export const LOW_THRESHOLD = 500;

export function creditState(balance: number): CreditState {
  if (balance <= CREDIT_FLOOR) return 'blocked';
  if (balance < 0) return 'overdrawn';
  if (balance <= LOW_THRESHOLD) return 'low';
  return 'normal';
}
